/**
 * Sanctum Dungeon Rendering System
 * 
 * Handles rendering of dungeon tiles, enemies, and loot containers.
 * Reuses existing visual systems for cohesion with ocean scene.
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '../types';
import { FogOfWarSystem } from './FogOfWarSystem';

// Architect Memory Lattice color palettes (numeric values for Phaser)
// Terminal colors - rusty bronze aesthetic matching ArchitectConduit UI
const TERMINAL_COLORS = {
  BORDER: 0x8B6F47,           // #8B6F47 - main border
  BORDER_LIGHT: 0xA0826D,     // #A0826D - lighter border
  ACCENT: 0xD4A574,           // #D4A574 - main accent
  ACCENT_LIGHT: 0xE8C4A0,     // #E8C4A0 - lighter accent
  TEXT: 0xD4A574,             // #D4A574 - text color
  TEXT_DARK: 0x8B6F47,        // #8B6F47 - dark text
  DARK_BG: 0x1a1410,          // #1a1410 - dark background
  METAL_BG: 0x2a2420,         // #2a2420 - metal background
};

// Base palette - used as fallback
const SANCTUM_COLORS_BASE = {
  LATTICE_TEAL: 0x8B6F47,
  LATTICE_TEAL_DARK: 0x5C4033,
  LATTICE_CYAN: 0xD4A574,
  LATTICE_PURPLE: 0xA0826D,
  STEEL_GRAY: 0x708090,
  DARK_GRAY: 0x2F4F4F,
  RUST_ORANGE: 0xB7410E,
  FLOOR_LIGHT: 0xB8C5D0,
  FLOOR_MEDIUM: 0xA0B0C0,
  FLOOR_DARK: 0x8899AA,
  FLOOR_DAMAGED: 0x7A7268,
  WALL_STONE_GREY: 0x3A3A3A,
  WALL_BORDER: 0x001F5F,
  INNER_WALL_DARK: 0x2A2A2A,
  PILLAR_DARK: 0x1A1A1A,
  CRATE_BROWN: 0x5C4033,
  DEBRIS_GRAY: 0x404040,
};

// Survival Sanctum palette - water damage, decay aesthetic
const SANCTUM_COLORS_SURVIVAL = {
  ...SANCTUM_COLORS_BASE,
  FLOOR_LIGHT: 0xA8B5C0,       // Lighter, water-stained
  FLOOR_MEDIUM: 0x8A9AAA,      // Blue-tinted
  FLOOR_DARK: 0x7A8A9A,        // Darker water stain
  FLOOR_DAMAGED: 0x6A7A8A,     // More water damage
  WALL_STONE_GREY: 0x4A5A6A,   // Lighter stone (weathered)
  INNER_WALL_DARK: 0x3A4A5A,   // Lighter inner walls
  PILLAR_DARK: 0x2A3A4A,       // Lighter pillars
  HAZARD_COLOR: 0x4A7A9A,      // Water hazards (blue)
};

// Experiment Sanctum palette - rift corruption, dark aesthetic
const SANCTUM_COLORS_EXPERIMENT = {
  ...SANCTUM_COLORS_BASE,
  FLOOR_LIGHT: 0x8A7A9A,       // Purple-tinted light
  FLOOR_MEDIUM: 0x6A5A8A,      // Darker purple
  FLOOR_DARK: 0x5A4A7A,        // Deep purple
  FLOOR_DAMAGED: 0x4A3A6A,     // Corrupted
  WALL_STONE_GREY: 0x2A2A3A,   // Darker walls
  INNER_WALL_DARK: 0x1A1A2A,   // Very dark inner
  PILLAR_DARK: 0x0A0A1A,       // Nearly black pillars
  HAZARD_COLOR: 0x8B00FF,      // Rift hazards (purple)
};

// Preservation Sanctum palette - gold/bronze accents, ancient archive
const SANCTUM_COLORS_PRESERVATION = {
  ...SANCTUM_COLORS_BASE,
  FLOOR_LIGHT: 0x9A8A7A,       // Bronze-tinted light
  FLOOR_MEDIUM: 0x8A7A6A,      // Polished bronze
  FLOOR_DARK: 0x7A6A5A,        // Darker bronze
  FLOOR_DAMAGED: 0x6A5A4A,     // Less damage (maintained)
  WALL_STONE_GREY: 0x4A3A2A,   // Bronze-tinted walls
  INNER_WALL_DARK: 0x3A2A1A,   // Bronze inner walls
  PILLAR_DARK: 0x2A1A0A,       // Bronze pillars
  HAZARD_COLOR: 0xD4AF37,      // Gold hazards (rare)
};

// Function to get color palette based on sanctum type
const getSanctumColorPalette = (sanctumType?: 'survival' | 'experiment' | 'preservation') => {
  switch (sanctumType) {
    case 'survival':
      return SANCTUM_COLORS_SURVIVAL;
    case 'experiment':
      return SANCTUM_COLORS_EXPERIMENT;
    case 'preservation':
      return SANCTUM_COLORS_PRESERVATION;
    default:
      return SANCTUM_COLORS_BASE;
  }
};

let SANCTUM_COLORS = SANCTUM_COLORS_BASE;

// Tile type constants (matching server-side)
const TILE_TYPES = {
  FLOOR: 1,
  WALL: 2,
  DOOR: 3,
  HAZARD: 4,
  TERMINAL: 5,
  DAMAGED_FLOOR: 6,
  CRATE: 7,
  DEBRIS: 8,
  CONSOLE: 9,
  PIPE: 10,
  INNER_WALL: 11,
  PILLAR: 12,
} as const;

interface SanctumRoom {
  id: string;
  type: 'entry' | 'combat' | 'overseer_office' | 'boss' | 'treasure';
  width: number;
  height: number;
  tileX: number;
  tileY: number;
  tiles: number[][];
  collisionMap: boolean[][];
  enemies: SanctumEnemy[];
  lootContainers: SanctumLootContainer[];
  steamPipes?: any[];
  environmentalEffects?: any[];
}

interface SanctumEnemy {
  id: string;
  enemyType: string;
  tileX: number;
  tileY: number;
  hp: number;
  maxHp: number;
  defeated: boolean;
  spriteKey: string;
}

interface SanctumLootContainer {
  id: string;
  tileX: number;
  tileY: number;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  looted: boolean;
  spriteKey: string;
}

interface CorridorData {
  id: string;
  fromRoomId: string;
  toRoomId: string;
  tiles: Array<{ tileX: number; tileY: number }>;
}

export interface DungeonLayout {
  rooms: SanctumRoom[];
  corridors: CorridorData[];
  entryRoomId: string;
  bossRoomId: string;
  overseerOfficeRoomId: string;
}

interface EnemySprite extends Phaser.GameObjects.Container {
  enemyId: string;
  enemyData: SanctumEnemy;
  sprite: Phaser.GameObjects.Arc;
  hpBar: Phaser.GameObjects.Graphics;
  nameLabel: Phaser.GameObjects.Text;
}

interface ContainerSprite extends Phaser.GameObjects.Container {
  containerId: string;
  containerData: SanctumLootContainer;
  sprite: Phaser.GameObjects.Rectangle;
  glowEffect?: Phaser.GameObjects.Arc;
}

export interface SanctumRendererCallbacks {
  onEnemyClick?: (enemyId: string) => void;
  onContainerClick?: (containerId: string) => void;
  onContainerRightClick?: (containerId: string, screenX: number, screenY: number) => void;
  onTileClick?: (tileX: number, tileY: number) => void;
  onTerminalClick?: (roomId: string) => void;
  onExitClick?: () => void;
}

export class SanctumRenderer {
  private scene: Phaser.Scene;
  private callbacks: SanctumRendererCallbacks;
  
  // Rendering containers
  private tileLayer: Phaser.GameObjects.Container | null = null;
  private enemyLayer: Phaser.GameObjects.Container | null = null;
  private containerLayer: Phaser.GameObjects.Container | null = null;
  private overheadLayer: Phaser.GameObjects.Container | null = null;
  
  private tileGraphics: Phaser.GameObjects.Graphics | null = null;
  private voidGraphics: Phaser.GameObjects.Graphics | null = null;
  private fogGraphics: Phaser.GameObjects.Graphics | null = null;
  private fogOfWar: FogOfWarSystem;
  private currentDungeonLayout: DungeonLayout | null = null;
  private currentRoom: SanctumRoom | null = null;
  private isActive: boolean = false;
  private sanctumType: 'survival' | 'experiment' | 'preservation' | null = null;
  
  // Sprite tracking
  private enemies: Map<string, EnemySprite> = new Map();
  private containers: Map<string, ContainerSprite> = new Map();
  private terminalSprite: Phaser.GameObjects.Container | null = null;
  private exitPortal: Phaser.GameObjects.Container | null = null;
  private collisionMap: Map<string, boolean> = new Map();
  
  // Boss telegraph tracking
  private telegraphLayer: Phaser.GameObjects.Container | null = null;
  private activeTelegraphs: Map<string, Phaser.GameObjects.Graphics> = new Map();
  
  // Overhead environmental elements (cables, pipes) with parallax
  private overheadElements: Phaser.GameObjects.Graphics[] = [];
  private lastCameraX: number = 0;
  private lastCameraY: number = 0;
  
  // Steam pipe effects
  private steamPipeEffects: Map<string, { graphics: Phaser.GameObjects.Graphics; particles: Phaser.GameObjects.Graphics[] }> = new Map();
  private steamParticleTimers: Map<string, number> = new Map();
  
  // Wall light effects
  private wallLights: Map<string, Phaser.GameObjects.Graphics> = new Map();
  private lightOverlay: Phaser.GameObjects.Graphics | null = null;

  constructor(scene: Phaser.Scene, callbacks: SanctumRendererCallbacks = {}) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.fogOfWar = new FogOfWarSystem(12); // 12 tile view radius
  }

  /**
   * Initialize sanctum rendering layers
   */
  initialize(): void {
    // Create layered containers for proper depth sorting
    // Void background at depth 5 (below dungeon tiles)
    this.voidGraphics = this.scene.add.graphics();
    this.voidGraphics.setDepth(5); // Below dungeon tiles
    
    // Tiles at depth 10 (above void, above ocean water at 0), player ship will be at depth 1000
    this.tileLayer = this.scene.add.container(0, 0);
    this.tileLayer.setDepth(10);
    
    // Tile graphics for rendering dungeon tiles
    this.tileGraphics = this.scene.add.graphics();
    this.tileGraphics.setDepth(10);
    
    // Enemies at depth 15 (above tiles)
    this.enemyLayer = this.scene.add.container(0, 0);
    this.enemyLayer.setDepth(15);
    
    // Loot containers at depth 20 (above enemies)
    this.containerLayer = this.scene.add.container(0, 0);
    this.containerLayer.setDepth(20);
    
    // Overhead environmental layer at depth 1100 (above player at 1000, slightly transparent)
    this.overheadLayer = this.scene.add.container(0, 0);
    this.overheadLayer.setDepth(1100);
    this.overheadLayer.setAlpha(0.6); // Slightly faded to show layer above player
    
    // Boss telegraph layer at depth 1500 (above player at 1000)
    this.telegraphLayer = this.scene.add.container(0, 0);
    this.telegraphLayer.setDepth(1500);
    
    // Fog of war at depth 2000 (above everything including player)
    this.fogGraphics = this.scene.add.graphics();
    this.fogGraphics.setDepth(2000); // Above everything including player
    
    console.log('🏛️ SanctumRenderer initialized with overhead environmental layer at depth 1100');
  }

  /**
   * Get all containers for click detection
   */
  getContainers(): Map<string, { x: number; y: number }> {
    return this.containers;
  }

  /**
   * Set sanctum type and update color palette
   */
  setSanctumType(type: 'survival' | 'experiment' | 'preservation'): void {
    this.sanctumType = type;
    SANCTUM_COLORS = getSanctumColorPalette(type);
    console.log(`🏛️ Sanctum type set to: ${type}`);
  }

  /**
   * Activate sanctum rendering (hide ocean, show dungeon)
   */
  activate(): void {
    if (!this.tileLayer || !this.enemyLayer || !this.containerLayer) {
      this.initialize();
    }
    
    this.isActive = true;
    this.tileLayer!.setVisible(true);
    this.enemyLayer!.setVisible(true);
    this.containerLayer!.setVisible(true);
    
    console.log('🏛️ Sanctum rendering activated');
  }

  /**
   * Deactivate sanctum rendering (show ocean, hide dungeon)
   */
  deactivate(): void {
    this.isActive = false;
    
    // Hide all layers
    if (this.tileLayer) this.tileLayer.setVisible(false);
    if (this.enemyLayer) this.enemyLayer.setVisible(false);
    if (this.containerLayer) this.containerLayer.setVisible(false);
    
    // Clear all rendered content
    this.clearRoom();
    
    // Clear dungeon layout and graphics completely
    this.currentDungeonLayout = null;
    this.collisionMap.clear();
    
    // Clear tile graphics (dungeon tiles)
    if (this.tileGraphics) {
      this.tileGraphics.clear();
    }
    
    // Clear void graphics (black background)
    if (this.voidGraphics) {
      this.voidGraphics.clear();
    }
    
    // Clear fog of war graphics
    if (this.fogGraphics) {
      this.fogGraphics.clear();
    }
    
    // Clear exit portal
    if (this.exitPortal) {
      this.exitPortal.destroy();
      this.exitPortal = null;
    }
    
    // Reset fog of war data
    if (this.fogOfWar) {
      this.fogOfWar.reset();
    }
    
    console.log('🏛️ Sanctum rendering fully deactivated and cleared');
  }

  /**
   * Render a single sanctum room (legacy method)
   */
  renderRoom(room: SanctumRoom): void {
    this.currentRoom = room;
    
    // Clear previous room
    this.clearRoom();
    
    // Render tiles
    this.renderTiles(room);
    
    // Render enemies
    room.enemies.forEach((enemy: SanctumEnemy) => {
      if (!enemy.defeated) {
        this.renderEnemy(enemy);
      }
    });
    
    // Render loot containers
    room.lootContainers.forEach((container: SanctumLootContainer) => {
      if (!container.looted) {
        this.renderContainerInRoom(container, room);
      }
    });
    
    // Render terminal if room has one (entry room or overseer's office)
    this.renderTerminalIfPresent(room);
    
    console.log(`🏛️ Rendered ${room.type} room: ${room.width}x${room.height} at (${room.tileX}, ${room.tileY}), ${room.enemies.length} enemies, ${room.lootContainers.length} containers`);
    console.log(`🏛️ Room world bounds: (${room.tileX * TILE_SIZE}, ${room.tileY * TILE_SIZE}) to (${(room.tileX + room.width) * TILE_SIZE}, ${(room.tileY + room.height) * TILE_SIZE})`);
  }

  /**
   * Render full dungeon layout with all rooms and corridors
   */
  renderDungeon(layout: DungeonLayout, sanctumGrade?: 'A' | 'B' | 'C' | 'D'): void {
    console.log(`🏛️ Rendering full dungeon: ${layout.rooms.length} rooms, ${layout.corridors.length} corridors (Grade: ${sanctumGrade || 'D'})`);
    
    // Store layout for fog of war
    this.currentDungeonLayout = layout;
    
    // Build collision map for fog of war
    this.buildCollisionMap(layout);
    
    // Reset fog of war for new dungeon
    this.fogOfWar.reset();
    
    // Clear everything first
    this.clearRoom();
    
    if (!this.tileGraphics) return;
    
    // Render black void background to clearly show dungeon boundaries
    this.renderVoidBackground(layout);
    
    // First render all corridors (below rooms)
    layout.corridors.forEach((corridor: CorridorData) => {
      this.renderCorridorTiles(corridor);
    });
    
    // Then render all rooms
    layout.rooms.forEach((room: SanctumRoom) => {
      this.renderRoomTiles(room);
      
      // NOTE: Enemies are now rendered as NPC ships via ShipRenderer, not placeholder sprites
      // The ship system will handle enemy rendering, movement, and combat
      
      // Render loot containers in this room
      room.lootContainers.forEach((container: SanctumLootContainer) => {
        if (!container.looted) {
          this.renderContainerInRoom(container, room);
        }
      });
      
      // Render steam pipes with particle effects
      if (room.steamPipes) {
        room.steamPipes.forEach((pipe: any) => {
          this.renderSteamPipe(pipe, room);
        });
      }
      
      // Render terminal if room has one (entry room or overseer's office)
      this.renderTerminalIfPresent(room);
    });
    
    // Store entry room as current
    const entryRoom = layout.rooms.find(r => r.id === layout.entryRoomId);
    if (entryRoom) {
      this.currentRoom = entryRoom;
      // Render exit portal in entry room
      this.renderExitPortal(entryRoom);
    }
    
    // Render sanctum grade indicator in top-left corner
    if (sanctumGrade) {
      this.renderSanctumGradeIndicator(sanctumGrade);
    }
    
    // Render steam vents with occasional leaks
    this.renderSteamVents(layout);
    
    // Render wall lights with simulated light effects
    this.renderWallLights(layout);
    
    // Render environmental effects (sparks, drips, fire, etc)
    this.renderEnvironmentalEffects(layout);
    
    console.log('🏛️ Full dungeon rendered successfully (enemies rendered via ship system)');
  }

  /**
   * Render Sanctum Grade indicator (A/B/C/D) with visual distinction
   */
  private renderSanctumGradeIndicator(grade: 'A' | 'B' | 'C' | 'D'): void {
    if (!this.scene) return;
    
    const gradeColors: Record<string, number> = {
      'A': 0xFF6B6B, // Red - hardest
      'B': 0xFFA500, // Orange - hard
      'C': 0xFFD700, // Gold - medium
      'D': 0x90EE90, // Light green - easiest
    };
    
    const gradeGlows: Record<string, number> = {
      'A': 0xFF0000,
      'B': 0xFF8C00,
      'C': 0xFFD700,
      'D': 0x00AA00,
    };
    
    const x = 40;
    const y = 40;
    const size = 30;
    
    // Background circle
    const bg = this.scene.add.circle(x, y, size + 5, 0x000000, 0.7);
    bg.setScrollFactor(0);
    bg.setDepth(1000);
    
    // Grade circle with glow
    const circle = this.scene.add.circle(x, y, size, gradeColors[grade], 1);
    circle.setScrollFactor(0);
    circle.setDepth(1001);
    circle.setStrokeStyle(3, gradeGlows[grade], 1);
    
    // Grade text
    const text = this.scene.add.text(x, y, grade, {
      fontSize: '24px',
      fontFamily: 'Arial',
      color: '#FFFFFF',
      fontStyle: 'bold',
      align: 'center',
    });
    text.setOrigin(0.5);
    text.setScrollFactor(0);
    text.setDepth(1002);
    
    // Pulsing animation for grade indicator
    this.scene.tweens.add({
      targets: circle,
      alpha: { from: 1, to: 0.7 },
      duration: 1500,
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Apply fog of war atmosphere based on sanctum type
   */
  private applyFogOfWarAtmosphere(): void {
    if (!this.fogGraphics) return;
    
    const fogOpacity = this.getFogOpacityBySanctumType();
    const fogColor = this.getFogColorBySanctumType();
    
    // Clear previous fog
    this.fogGraphics.clear();
    
    // Apply fog overlay with sanctum-type-specific opacity and color
    this.fogGraphics.fillStyle(fogColor, fogOpacity);
    this.fogGraphics.fillRect(-10000, -10000, 20000, 20000);
  }

  /**
   * Get fog opacity based on sanctum type
   */
  private getFogOpacityBySanctumType(): number {
    switch (this.sanctumType) {
      case 'survival':
        return 0.2; // Light fog (water vapor)
      case 'experiment':
        return 0.5; // Heavy fog (rift corruption)
      case 'preservation':
        return 0.05; // Minimal fog (well-maintained)
      default:
        return 0.3;
    }
  }

  /**
   * Get fog color based on sanctum type
   */
  private getFogColorBySanctumType(): number {
    switch (this.sanctumType) {
      case 'survival':
        return 0x4A7A9A; // Blue fog (water)
      case 'experiment':
        return 0x8B00FF; // Purple fog (rift)
      case 'preservation':
        return 0xD4AF37; // Gold fog (archive)
      default:
        return 0x000000; // Black fog
    }
  }

  /**
   * Render portal variant based on feature type (sealed door, breach, vault)
   */
  renderPortalVariant(worldX: number, worldY: number, featureType: 'sealed_door' | 'containment_breach' | 'archive_vault'): void {
    if (!this.scene) return;
    
    const container = this.scene.add.container(worldX, worldY);
    
    const portalConfig = this.getPortalVariantConfig(featureType);
    
    // Draw portal circle
    const portal = this.scene.add.circle(0, 0, 20, portalConfig.fillColor, 0.7);
    portal.setStrokeStyle(3, portalConfig.glowColor, 1);
    container.add(portal);
    
    // Add pulsing animation
    this.scene.tweens.add({
      targets: portal,
      alpha: { from: 0.7, to: 0.4 },
      duration: 1500,
      yoyo: true,
      repeat: -1,
    });
    
    // Add glow effect
    const glow = this.scene.add.circle(0, 0, 22, portalConfig.glowColor, 0.2);
    container.add(glow);
    
    this.scene.tweens.add({
      targets: glow,
      radius: 28,
      alpha: 0,
      duration: 1500,
      yoyo: true,
      repeat: -1,
    });
    
    // Add icon/text based on feature type
    const icon = this.scene.add.text(0, 0, portalConfig.icon, {
      fontSize: '16px',
      fontFamily: 'Arial',
      color: portalConfig.textColor,
      fontStyle: 'bold',
    });
    icon.setOrigin(0.5);
    container.add(icon);
    
    this.containerLayer?.add(container);
  }

  /**
   * Get portal variant configuration based on feature type
   */
  private getPortalVariantConfig(featureType: 'sealed_door' | 'containment_breach' | 'archive_vault'): {
    fillColor: number;
    glowColor: number;
    icon: string;
    textColor: string;
  } {
    switch (featureType) {
      case 'sealed_door':
        return {
          fillColor: 0xFF6B6B,      // Red
          glowColor: 0xFF0000,       // Bright red
          icon: '🚪',
          textColor: '#FFFFFF',
        };
      case 'containment_breach':
        return {
          fillColor: 0x8B00FF,       // Purple
          glowColor: 0xFF00FF,       // Bright magenta
          icon: '⚡',
          textColor: '#FFFFFF',
        };
      case 'archive_vault':
        return {
          fillColor: 0xD4AF37,       // Gold
          glowColor: 0xFFD700,       // Bright gold
          icon: '📦',
          textColor: '#000000',
        };
    }
  }

  /**
   * Render black void background for entire dungeon area
   * This makes it clear where the dungeon boundaries are
   * Uses separate graphics object at lower z-index so dungeon tiles appear on top
   */
  private renderVoidBackground(layout: DungeonLayout): void {
    if (!this.voidGraphics) return;
    
    // Clear previous void rendering
    this.voidGraphics.clear();
    
    // Don't render void background at all - let the ocean background show through
    // The dungeon tiles will render on top of the ocean, and the void will be the ocean itself
    // This prevents the black void from covering dungeon tiles
    
    console.log(`🏛️ Void background disabled - ocean background will show in empty areas`);
  }

  /**
   * Render corridor tiles with walls around them
   */
  private renderCorridorTiles(corridor: CorridorData): void {
    if (!this.tileGraphics) return;
    
    // Build set of corridor floor positions for quick lookup
    const floorPositions = new Set<string>();
    corridor.tiles.forEach((tile: { tileX: number; tileY: number }) => {
      floorPositions.add(`${tile.tileX},${tile.tileY}`);
    });
    
    // Track wall positions to avoid duplicates
    const wallPositions = new Set<string>();
    
    // Get dungeon bounds from current layout
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    if (this.currentDungeonLayout) {
      this.currentDungeonLayout.rooms.forEach((room: SanctumRoom) => {
        minX = Math.min(minX, room.tileX);
        minY = Math.min(minY, room.tileY);
        maxX = Math.max(maxX, room.tileX + room.width);
        maxY = Math.max(maxY, room.tileY + room.height);
      });
      // Add padding for corridors
      const padding = 5;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;
    }
    
    // For each floor tile, check neighbors and add walls where needed
    corridor.tiles.forEach((tile: { tileX: number; tileY: number }) => {
      // Check all 8 neighbors
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          if (dx === 0 && dy === 0) continue; // Skip self
          
          const neighborX = tile.tileX + dx;
          const neighborY = tile.tileY + dy;
          const neighborKey = `${neighborX},${neighborY}`;
          
          // Skip if outside dungeon bounds
          if (minX !== Infinity && (neighborX < minX || neighborX >= maxX || neighborY < minY || neighborY >= maxY)) {
            continue;
          }
          
          // If neighbor is not a floor tile and not already a wall, add wall
          if (!floorPositions.has(neighborKey) && !wallPositions.has(neighborKey)) {
            wallPositions.add(neighborKey);
            
            const wallX = neighborX * TILE_SIZE;
            const wallY = neighborY * TILE_SIZE;
            
            // Render wall tile with dark stone grey
            this.tileGraphics!.fillStyle(SANCTUM_COLORS.WALL_STONE_GREY, 1);
            this.tileGraphics!.fillRect(wallX, wallY, TILE_SIZE, TILE_SIZE);
          }
        }
      }
    });
    
    // Render floor tiles on top of walls - use same variety as room floors
    corridor.tiles.forEach((tile: { tileX: number; tileY: number }) => {
      const worldX = tile.tileX * TILE_SIZE;
      const worldY = tile.tileY * TILE_SIZE;
      
      // Use same pseudo-random floor variety as room tiles
      const seedValue = (worldX + worldY * 7919) % 100;
      let fillColor: number;
      
      if (seedValue < 20) {
        fillColor = SANCTUM_COLORS.FLOOR_DARK;
      } else if (seedValue < 40) {
        fillColor = SANCTUM_COLORS.FLOOR_MEDIUM;
      } else if (seedValue < 60) {
        fillColor = SANCTUM_COLORS.FLOOR_DAMAGED;
      } else {
        fillColor = SANCTUM_COLORS.STEEL_GRAY;
      }
      
      this.tileGraphics!.fillStyle(fillColor, 1);
      this.tileGraphics!.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
    });
  }

  /**
   * Render room tiles (used by renderDungeon)
   */
  private renderRoomTiles(room: SanctumRoom): void {
    if (!this.tileGraphics) return;
    
    for (let y = 0; y < room.height; y++) {
      for (let x = 0; x < room.width; x++) {
        const tileType = room.tiles[y][x];
        const isWalkable = !room.collisionMap[y][x];
        
        const worldX = (room.tileX + x) * TILE_SIZE;
        const worldY = (room.tileY + y) * TILE_SIZE;
        
        this.renderTile(worldX, worldY, tileType, isWalkable);
      }
    }
    
    // Add room label
    // const labelX = (room.tileX + room.width / 2) * TILE_SIZE;
    // const labelY = room.tileY * TILE_SIZE - 20;
    // const roomLabel = this.scene.add.text(labelX, labelY, this.getRoomLabel(room.type), {
    //   fontSize: '14px',
    //   color: '#FFD700',
    //   backgroundColor: '#003DA5',
    //   padding: { x: 8, y: 4 },
    //   fontFamily: '"Sometype Mono", monospace',
    //   fontStyle: 'bold',
    // });
    // roomLabel.setOrigin(0.5);
    // this.tileLayer!.add(roomLabel);
  }

  /**
   * Get display label for room type
   */
  private getRoomLabel(type: string): string {
    switch (type) {
      case 'entry': return 'ENTRY';
      case 'combat': return 'COMBAT';
      case 'treasure': return 'TREASURE';
      case 'overseer_office': return 'OVERSEER';
      case 'boss': return 'BOSS';
      default: return type.toUpperCase();
    }
  }

  /**
   * Render enemy in a specific room context
   */
  private renderEnemyInRoom(enemy: SanctumEnemy, room: SanctumRoom): void {
    const worldX = (room.tileX + enemy.tileX) * TILE_SIZE + TILE_SIZE / 2;
    const worldY = (room.tileY + enemy.tileY) * TILE_SIZE + TILE_SIZE / 2;
    
    const container = this.scene.add.container(worldX, worldY) as EnemySprite;
    container.enemyId = enemy.id;
    container.enemyData = enemy;
    
    const enemyColor = this.getEnemyColor(enemy.enemyType);
    const sprite = this.scene.add.circle(0, 0, TILE_SIZE * 0.3, enemyColor);
    sprite.setStrokeStyle(2, 0xFF0000, 1);
    container.sprite = sprite;
    container.add(sprite);
    
    const hpBar = this.scene.add.graphics();
    this.updateEnemyHPBar(hpBar, enemy.hp, enemy.maxHp);
    container.hpBar = hpBar;
    container.add(hpBar);
    
    const nameLabel = this.scene.add.text(0, -TILE_SIZE * 0.6, enemy.enemyType.replace('_', ' ').toUpperCase(), {
      fontSize: '10px',
      color: '#ffffff',
      backgroundColor: '#000000',
      padding: { x: 4, y: 2 },
      fontFamily: '"Sometype Mono", monospace',
    });
    nameLabel.setOrigin(0.5);
    container.nameLabel = nameLabel;
    container.add(nameLabel);
    
    sprite.setInteractive({ useHandCursor: true });
    sprite.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.leftButtonDown() && this.callbacks.onEnemyClick) {
        this.callbacks.onEnemyClick(enemy.id);
      }
    });
    
    this.enemyLayer!.add(container);
    this.enemies.set(enemy.id, container);
  }

  /**
   * Render container in a specific room context
   */
  private renderContainerInRoom(container: SanctumLootContainer, room: SanctumRoom): void {
    const worldX = (room.tileX + container.tileX) * TILE_SIZE + TILE_SIZE / 2;
    const worldY = (room.tileY + container.tileY) * TILE_SIZE + TILE_SIZE / 2;
    
    const containerSprite = this.scene.add.container(worldX, worldY) as ContainerSprite;
    containerSprite.containerId = container.id;
    containerSprite.containerData = container;
    
    // Use same visual as NPC-dropped containers - gold square with orange border and glow
    const graphics = this.scene.add.graphics();
    graphics.fillStyle(0xffd700, 1); // Gold fill
    graphics.fillRect(-4, -4, 8, 8);
    graphics.lineStyle(1, 0xffaa00, 1); // Orange border
    graphics.strokeRect(-4, -4, 8, 8);
    graphics.setPosition(0, 0);
    
    containerSprite.sprite = graphics as any;
    containerSprite.add(graphics);
    
    // Add glow effect (same as NPC-dropped)
    const glow = this.scene.add.graphics();
    glow.lineStyle(1, 0xffd700, 0.5);
    glow.strokeRect(-6, -6, 12, 12);
    glow.setPosition(0, 0);
    containerSprite.glowEffect = glow as any;
    containerSprite.add(glow);
    
    // Make interactive with both left-click and right-click (event-based like resource nodes)
    const hitArea = this.scene.add.rectangle(0, 0, TILE_SIZE, TILE_SIZE, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    
    // Handle both left and right clicks
    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
      
      if (pointer.leftButtonDown()) {
        // Left-click: Show context menu (same as right-click for pre-placed containers)
        console.log(`🏛️ Left-click on pre-placed container: ${container.id}, showing context menu`);
        const camera = this.scene.cameras.main;
        const screenX = (worldX - camera.scrollX) * camera.zoom;
        const screenY = (worldY - camera.scrollY) * camera.zoom;
        
        this.scene.events.emit('shipwreck:contextmenu', {
          wreckId: container.id,
          worldX: worldX,
          worldY: worldY,
          screenX: screenX,
          screenY: screenY,
          lootQuality: 'private',
          isSanctumContainer: true,
          containerData: container,
        });
      } else if (pointer.rightButtonDown()) {
        // Right-click: Show context menu
        console.log(`🏛️ Right-click on pre-placed container: ${container.id}, showing context menu`);
        const camera = this.scene.cameras.main;
        const screenX = (worldX - camera.scrollX) * camera.zoom;
        const screenY = (worldY - camera.scrollY) * camera.zoom;
        
        this.scene.events.emit('shipwreck:contextmenu', {
          wreckId: container.id,
          worldX: worldX,
          worldY: worldY,
          screenX: screenX,
          screenY: screenY,
          lootQuality: 'private',
          isSanctumContainer: true,
          containerData: container,
        });
      }
    });
    
    containerSprite.add(hitArea);
    
    this.containerLayer!.add(containerSprite);
    this.containers.set(container.id, containerSprite);
  }

  /**
   * Render dungeon tiles with Vault-Tec aesthetic
   */
  private renderTiles(room: SanctumRoom): void {
    if (!this.tileGraphics) return;
    
    this.tileGraphics.clear();
    
    for (let y = 0; y < room.height; y++) {
      for (let x = 0; x < room.width; x++) {
        const tileType = room.tiles[y][x];
        const isWalkable = !room.collisionMap[y][x];
        
        const worldX = (room.tileX + x) * TILE_SIZE;
        const worldY = (room.tileY + y) * TILE_SIZE;
        
        this.renderTile(worldX, worldY, tileType, isWalkable);
      }
    }
  }

  /**
   * Render a single tile based on type
   */
  private renderTile(x: number, y: number, tileType: number, isWalkable: boolean): void {
    if (!this.tileGraphics) return;
    
    let fillColor: number;
    
    // Pseudo-random seed based on position for consistent variety
    const seedValue = (x + y * 7919) % 100;
    
    switch (tileType) {
      case TILE_TYPES.FLOOR:
        // Randomize floor tile shades for variety
        if (seedValue < 20) {
          fillColor = SANCTUM_COLORS.FLOOR_DARK;
        } else if (seedValue < 40) {
          fillColor = SANCTUM_COLORS.FLOOR_MEDIUM;
        } else if (seedValue < 60) {
          fillColor = SANCTUM_COLORS.FLOOR_DAMAGED;
        } else {
          fillColor = SANCTUM_COLORS.STEEL_GRAY;
        }
        break;
      
      case TILE_TYPES.WALL:
        // Primary walls use dark stone grey
        fillColor = SANCTUM_COLORS.WALL_STONE_GREY;
        break;
      
      case TILE_TYPES.DOOR:
        fillColor = SANCTUM_COLORS.LATTICE_CYAN;
        break;
      
      case TILE_TYPES.HAZARD:
        fillColor = SANCTUM_COLORS.RUST_ORANGE;
        break;
      
      case TILE_TYPES.TERMINAL:
        fillColor = SANCTUM_COLORS.LATTICE_TEAL;
        break;
      
      case TILE_TYPES.DAMAGED_FLOOR:
        fillColor = SANCTUM_COLORS.FLOOR_DAMAGED;
        break;
      
      case 7: // CRATE - interior decorative
        fillColor = SANCTUM_COLORS.CRATE_BROWN;
        break;
      
      case 8: // DEBRIS - interior decorative
        fillColor = SANCTUM_COLORS.DEBRIS_GRAY;
        break;
      
      case 9: // CONSOLE - interior decorative
        fillColor = SANCTUM_COLORS.LATTICE_TEAL_DARK;
        break;
      
      case 10: // PIPE - interior decorative
        fillColor = SANCTUM_COLORS.STEEL_GRAY;
        break;
      
      case TILE_TYPES.INNER_WALL:
        // Inner walls are darker than primary walls
        fillColor = SANCTUM_COLORS.INNER_WALL_DARK;
        break;
      
      case TILE_TYPES.PILLAR:
        // Pillars are darkest
        fillColor = SANCTUM_COLORS.PILLAR_DARK;
        break;
      
      default:
        fillColor = SANCTUM_COLORS.FLOOR_LIGHT;
    }
    
    // Draw tile as perfect square (no rounding, no borders)
    this.tileGraphics.fillStyle(fillColor, 1);
    this.tileGraphics.fillRect(x, y, TILE_SIZE, TILE_SIZE);
  }

  /**
   * Render an enemy sprite (reusing ship rendering style)
   */
  private renderEnemy(enemy: SanctumEnemy): void {
    if (!this.currentRoom) return;
    
    const worldX = (this.currentRoom.tileX + enemy.tileX) * TILE_SIZE + TILE_SIZE / 2;
    const worldY = (this.currentRoom.tileY + enemy.tileY) * TILE_SIZE + TILE_SIZE / 2;
    
    const container = this.scene.add.container(worldX, worldY) as EnemySprite;
    container.enemyId = enemy.id;
    container.enemyData = enemy;
    
    // Enemy sprite (circle similar to ship rendering)
    const enemyColor = this.getEnemyColor(enemy.enemyType);
    const sprite = this.scene.add.circle(0, 0, TILE_SIZE * 0.3, enemyColor);
    sprite.setStrokeStyle(2, 0xFF0000, 1);
    container.sprite = sprite;
    container.add(sprite);
    
    // HP bar (similar to ship HP bars)
    const hpBar = this.scene.add.graphics();
    this.updateEnemyHPBar(hpBar, enemy.hp, enemy.maxHp);
    container.hpBar = hpBar;
    container.add(hpBar);
    
    // Name label (hidden by default, shown on hover)
    const nameLabel = this.scene.add.text(0, -TILE_SIZE * 0.6, enemy.enemyType.replace('_', ' ').toUpperCase(), {
      fontSize: '10px',
      color: '#ffffff',
      backgroundColor: '#000000',
      padding: { x: 4, y: 2 },
      fontFamily: '"Sometype Mono", monospace',
    });
    nameLabel.setOrigin(0.5);
    nameLabel.setVisible(false); // Hidden by default
    container.nameLabel = nameLabel;
    container.add(nameLabel);
    
    // Show label on hover
    sprite.on('pointerover', () => {
      nameLabel.setVisible(true);
    });
    sprite.on('pointerout', () => {
      nameLabel.setVisible(false);
    });
    
    // Make interactive - left-click only
    sprite.setInteractive({ useHandCursor: true });
    sprite.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.leftButtonDown() && this.callbacks.onEnemyClick) {
        this.callbacks.onEnemyClick(enemy.id);
      }
    });
    
    this.enemyLayer!.add(container);
    this.enemies.set(enemy.id, container);
  }

  /**
   * Update enemy HP bar
   */
  private updateEnemyHPBar(graphics: Phaser.GameObjects.Graphics, hp: number, maxHp: number): void {
    graphics.clear();
    
    const barWidth = TILE_SIZE * 0.8;
    const barHeight = 4;
    const barY = TILE_SIZE * 0.5;
    
    // Background
    graphics.fillStyle(0x000000, 0.5);
    graphics.fillRect(-barWidth / 2, barY, barWidth, barHeight);
    
    // HP fill
    const hpPercent = hp / maxHp;
    const hpColor = hpPercent > 0.5 ? 0x00ff00 : hpPercent > 0.25 ? 0xffff00 : 0xff0000;
    graphics.fillStyle(hpColor, 1);
    graphics.fillRect(-barWidth / 2, barY, barWidth * hpPercent, barHeight);
    
    // Border
    graphics.lineStyle(1, 0xffffff, 0.8);
    graphics.strokeRect(-barWidth / 2, barY, barWidth, barHeight);
  }

  /**
   * Get enemy color based on type
   */
  private getEnemyColor(enemyType: string): number {
    switch (enemyType) {
      case 'vault_guard':
        return 0x0A4D4D; // Lattice teal
      case 'experiment_subject':
        return 0x8B0000; // Dark red
      case 'turret':
        return 0x708090; // Steel gray
      default:
        return 0xFF0000; // Red default
    }
  }

  /**
   * Render exit portal in entry room as rotating cog
   */
  private renderExitPortal(room: SanctumRoom): void {
    // Place exit portal in center of entry room
    const centerX = Math.floor(room.width / 2);
    const centerY = Math.floor(room.height / 2);
    const worldX = (room.tileX + centerX) * TILE_SIZE;
    const worldY = (room.tileY + centerY) * TILE_SIZE;
    
    const portal = this.scene.add.container(worldX + TILE_SIZE / 2, worldY + TILE_SIZE / 2);
    
    // Create rotating cog visual
    const cogRadius = TILE_SIZE * 0.9;
    const innerRadius = TILE_SIZE * 0.5;
    const teethCount = 12;
    const toothDepth = TILE_SIZE * 0.15;
    
    // Draw cog using graphics
    const cogGraphics = this.scene.add.graphics();
    
    // Outer cog with teeth
    cogGraphics.fillStyle(0x708090, 1); // Steel gray
    cogGraphics.lineStyle(2, 0x3A3A3A, 1); // Dark grey outline
    
    cogGraphics.beginPath();
    for (let i = 0; i < teethCount; i++) {
      const angle1 = (i / teethCount) * Math.PI * 2;
      const angle2 = ((i + 0.4) / teethCount) * Math.PI * 2;
      const angle3 = ((i + 0.6) / teethCount) * Math.PI * 2;
      const angle4 = ((i + 1) / teethCount) * Math.PI * 2;
      
      // Outer tooth edge
      const x1 = Math.cos(angle1) * cogRadius;
      const y1 = Math.sin(angle1) * cogRadius;
      const x2 = Math.cos(angle2) * cogRadius;
      const y2 = Math.sin(angle2) * cogRadius;
      
      // Inner tooth edge
      const x3 = Math.cos(angle3) * (cogRadius - toothDepth);
      const y3 = Math.sin(angle3) * (cogRadius - toothDepth);
      const x4 = Math.cos(angle4) * (cogRadius - toothDepth);
      const y4 = Math.sin(angle4) * (cogRadius - toothDepth);
      
      if (i === 0) {
        cogGraphics.moveTo(x1, y1);
      }
      cogGraphics.lineTo(x2, y2);
      cogGraphics.lineTo(x3, y3);
      cogGraphics.lineTo(x4, y4);
    }
    cogGraphics.closePath();
    cogGraphics.fillPath();
    cogGraphics.strokePath();
    
    // Inner circle (hole in cog)
    cogGraphics.fillStyle(0x1A1A1A, 1); // Dark center
    cogGraphics.fillCircle(0, 0, innerRadius);
    cogGraphics.lineStyle(2, 0x3A3A3A, 1);
    cogGraphics.strokeCircle(0, 0, innerRadius);
    
    // Add glowing center
    const glowCircle = this.scene.add.circle(0, 0, innerRadius * 0.7, 0xFFD700, 0.6);
    
    portal.add(cogGraphics);
    portal.add(glowCircle);
    
    // Continuous rotation animation
    this.scene.tweens.add({
      targets: cogGraphics,
      angle: 360,
      duration: 8000,
      repeat: -1,
      ease: 'Linear',
    });
    
    // Pulsing glow animation
    this.scene.tweens.add({
      targets: glowCircle,
      alpha: { from: 0.4, to: 0.8 },
      scale: { from: 0.9, to: 1.1 },
      duration: 1500,
      yoyo: true,
      repeat: -1,
    });
    
    // Make interactive - left-click only
    const hitArea = this.scene.add.circle(0, 0, cogRadius, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.leftButtonDown() && this.callbacks.onExitClick) {
        this.callbacks.onExitClick();
      }
    });
    portal.add(hitArea);
    
    this.containerLayer!.add(portal);
    this.exitPortal = portal;
  }

  /**
   * Spawn a loot container dynamically (e.g., from defeated NPC)
   */
  public spawnLootContainer(containerId: string, worldX: number, worldY: number, rarity: string = 'common'): void {
    if (!this.containerLayer) return;
    
    const containerSprite = this.scene.add.container(worldX, worldY) as ContainerSprite;
    containerSprite.containerId = containerId;
    containerSprite.containerData = {
      id: containerId,
      tileX: 0,
      tileY: 0,
      rarity: rarity as any,
      looted: false,
      spriteKey: 'chest',
    };
    
    // Use ocean loot container visual style (small gold square with glow) for consistency
    const graphics = this.scene.add.graphics();
    graphics.fillStyle(0xffd700, 1); // Gold fill
    graphics.fillRect(-4, -4, 8, 8);
    graphics.lineStyle(1, 0xffaa00, 1); // Orange border
    graphics.strokeRect(-4, -4, 8, 8);
    graphics.setPosition(0, 0);
    
    containerSprite.sprite = graphics as any;
    containerSprite.add(graphics);
    
    // Add glow effect (same as ocean)
    const glow = this.scene.add.graphics();
    glow.lineStyle(1, 0xffd700, 0.5);
    glow.strokeRect(-6, -6, 12, 12);
    glow.setPosition(0, 0);
    containerSprite.glowEffect = glow as any;
    containerSprite.add(glow);
    
    // Make interactive with proper hitArea (same pattern as renderContainer)
    const hitArea = this.scene.add.rectangle(0, 0, TILE_SIZE, TILE_SIZE, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    
    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
      
      if (pointer.leftButtonDown()) {
        // Left-click: Open loot panel (same as context menu "Open Loot")
        console.log(`🏛️ Left-click on loot container: ${containerId}, opening loot panel`);
        // Trigger the standard loot container open flow
        this.scene.game.events.emit('lootcontainer:open', { id: containerId });
        // Request the container data from server
        const socket = (this.scene as any).socket;
        if (socket) {
          socket.emit('lootcontainer:open', { id: containerId });
        }
      } else if (pointer.rightButtonDown()) {
        // Right-click: Show context menu
        console.log(`🏛️ Right-click on loot container: ${containerId}, showing context menu`);
        const camera = this.scene.cameras.main;
        const screenX = (worldX - camera.scrollX) * camera.zoom;
        const screenY = (worldY - camera.scrollY) * camera.zoom;
        
        this.scene.events.emit('shipwreck:contextmenu', {
          wreckId: containerId,
          worldX: worldX,
          worldY: worldY,
          screenX: screenX,
          screenY: screenY,
          lootQuality: 'private',
          isSanctumContainer: true,
        });
      }
    });
    
    containerSprite.add(hitArea);
    this.containerLayer.add(containerSprite);
    this.containers.set(containerId, containerSprite);
  }


  /**
   * Get loot container size and glow based on rarity
   */
  private getContainerRarityVariant(rarity: string): { size: number; glowColor: number; glowIntensity: number } {
    switch (rarity) {
      case 'common':
        return { size: 8, glowColor: 0xFFD700, glowIntensity: 0.3 };
      case 'uncommon':
        return { size: 10, glowColor: 0x00CED1, glowIntensity: 0.5 };
      case 'rare':
        return { size: 12, glowColor: 0x8B00FF, glowIntensity: 0.6 };
      case 'epic':
        return { size: 14, glowColor: 0xFF1493, glowIntensity: 0.8 };
      case 'legendary':
        return { size: 16, glowColor: 0xFFD700, glowIntensity: 1.0 };
      default:
        return { size: 8, glowColor: 0xFFD700, glowIntensity: 0.3 };
    }
  }

  /**
   * Draw container pattern based on rarity (like resource nodes)
   */
  private drawContainerPattern(graphics: Phaser.GameObjects.Graphics, rarity: string, x: number, y: number): void {
    const variant = this.getContainerRarityVariant(rarity);
    const size = variant.size;
    const offset = (16 - size) / 2;
    
    // Draw main container square with rarity-based size
    graphics.fillStyle(0xFFD700, 0.8);
    graphics.fillRect(x + offset, y + offset, size, size);
    
    // Draw glow border based on rarity
    graphics.lineStyle(2, variant.glowColor, variant.glowIntensity);
    graphics.strokeRect(x + offset - 1, y + offset - 1, size + 2, size + 2);
    
    // Add rarity-specific pattern
    switch (rarity) {
      case 'uncommon':
        // Cyan accent
        graphics.fillStyle(0x00CED1, 0.6);
        graphics.fillRect(x + offset + 2, y + offset + 2, 2, 2);
        graphics.fillRect(x + offset + size - 4, y + offset + size - 4, 2, 2);
        break;
      case 'rare':
        // Purple accents
        graphics.fillStyle(0x8B00FF, 0.6);
        graphics.fillRect(x + offset + 1, y + offset + 1, 2, 2);
        graphics.fillRect(x + offset + size - 3, y + offset + 1, 2, 2);
        graphics.fillRect(x + offset + 1, y + offset + size - 3, 2, 2);
        graphics.fillRect(x + offset + size - 3, y + offset + size - 3, 2, 2);
        break;
      case 'epic':
        // Pink diamond
        graphics.fillStyle(0xFF1493, 0.7);
        graphics.fillRect(x + offset + size / 2 - 1, y + offset - 1, 2, 2);
        graphics.fillRect(x + offset + size + 1, y + offset + size / 2 - 1, 2, 2);
        graphics.fillRect(x + offset + size / 2 - 1, y + offset + size + 1, 2, 2);
        graphics.fillRect(x + offset - 1, y + offset + size / 2 - 1, 2, 2);
        break;
      case 'legendary':
        // Gold star pattern
        graphics.fillStyle(0xFFD700, 1.0);
        graphics.fillRect(x + offset + size / 2 - 1, y + offset - 2, 2, 2);
        graphics.fillRect(x + offset + size + 2, y + offset + size / 2 - 1, 2, 2);
        graphics.fillRect(x + offset + size / 2 - 1, y + offset + size + 2, 2, 2);
        graphics.fillRect(x + offset - 2, y + offset + size / 2 - 1, 2, 2);
        break;
    }
  }

  /**
   * Map rarity to resource node type
   */
  private getRarityNodeType(rarity: string): string {
    switch (rarity) {
      case 'common':
        return 'stone_quarry';
      case 'uncommon':
        return 'tin_deposit';
      case 'rare':
        return 'silver_vein';
      case 'epic':
        return 'gold_deposit';
      case 'legendary':
        return 'void_crystal';
      default:
        return 'stone_quarry';
    }
  }

  /**
   * Get rarity color
   */
  private getRarityColor(rarity: string): number {
    switch (rarity) {
      case 'common':
        return 0x808080; // Gray
      case 'uncommon':
        return 0x00ff00; // Green
      case 'rare':
        return 0x0080ff; // Blue
      case 'epic':
        return 0x8000ff; // Purple
      case 'legendary':
        return 0xffa500; // Orange/Gold
      default:
        return 0xffffff; // White
    }
  }

  /**
   * Get bright border color for containers
   */
  private getBrightBorderColor(rarity: string): number {
    switch (rarity) {
      case 'common':
        return 0xcccccc; // Bright gray
      case 'uncommon':
        return 0x00ff00; // Bright green
      case 'rare':
        return 0x00ffff; // Bright cyan
      case 'epic':
        return 0xff00ff; // Bright magenta
      case 'legendary':
        return 0xffff00; // Bright yellow
      default:
        return 0xffffff; // White
    }
  }

  /**
   * Update enemy state (HP, defeated status)
   */
  updateEnemy(enemyId: string, hp: number, defeated: boolean): void {
    const enemySprite = this.enemies.get(enemyId);
    if (!enemySprite) return;
    
    if (defeated) {
      // Remove defeated enemy
      enemySprite.destroy();
      this.enemies.delete(enemyId);
    } else {
      // Update HP bar
      enemySprite.enemyData.hp = hp;
      this.updateEnemyHPBar(enemySprite.hpBar, hp, enemySprite.enemyData.maxHp);
    }
  }

  /**
   * Update container state (looted status)
   */
  updateContainer(containerId: string, looted: boolean): void {
    const containerSprite = this.containers.get(containerId);
    if (!containerSprite) return;
    
    if (looted) {
      // Fade out and remove looted container
      this.scene.tweens.add({
        targets: containerSprite,
        alpha: 0,
        duration: 500,
        onComplete: () => {
          containerSprite.destroy();
          this.containers.delete(containerId);
        },
      });
    }
  }

  /**
   * Check if room has a terminal tile and render it if present
   */
  private renderTerminalIfPresent(room: SanctumRoom): void {
    // Find terminal tile in room
    let terminalTileX = -1;
    let terminalTileY = -1;
    
    for (let y = 0; y < room.tiles.length; y++) {
      for (let x = 0; x < room.tiles[y].length; x++) {
        if (room.tiles[y][x] === TILE_TYPES.TERMINAL) {
          terminalTileX = x;
          terminalTileY = y;
          break;
        }
      }
      if (terminalTileX !== -1) break;
    }
    
    // If no terminal found, skip
    if (terminalTileX === -1) return;
    
    // Render terminal at the found location
    this.renderTerminalAtLocation(room, terminalTileX, terminalTileY);
  }

  /**
   * Render terminal at specific location in room
   */
  private renderTerminalAtLocation(room: SanctumRoom, localTileX: number, localTileY: number): void {
    const worldX = (room.tileX + localTileX) * TILE_SIZE + TILE_SIZE / 2;
    const worldY = (room.tileY + localTileY) * TILE_SIZE + TILE_SIZE / 2;
    
    this.renderTerminalVisuals(worldX, worldY, room.id);
  }

  /**
   * Render terminal visuals and interaction
   */
  private renderTerminalVisuals(worldX: number, worldY: number, roomId: string): void {
    
    const container = this.scene.add.container(worldX, worldY);

    // Draw custom retro-futuristic terminal using graphics
    const graphics = this.scene.add.graphics();
    
    // Outer frame - rusty bronze border
    graphics.lineStyle(3, TERMINAL_COLORS.BORDER, 1);
    graphics.strokeRect(-TILE_SIZE * 0.5, -TILE_SIZE * 0.5, TILE_SIZE, TILE_SIZE);
    
    // Inner frame - accent border
    graphics.lineStyle(1, TERMINAL_COLORS.ACCENT, 0.6);
    graphics.strokeRect(-TILE_SIZE * 0.45, -TILE_SIZE * 0.45, TILE_SIZE * 0.9, TILE_SIZE * 0.9);
    
    // Top panel - control area
    graphics.fillStyle(TERMINAL_COLORS.BORDER, 0.5);
    graphics.fillRect(-TILE_SIZE * 0.45, -TILE_SIZE * 0.45, TILE_SIZE * 0.9, TILE_SIZE * 0.15);
    
    // Screen area - glowing display
    graphics.fillStyle(TERMINAL_COLORS.ACCENT, 0.2);
    graphics.fillRect(-TILE_SIZE * 0.4, -TILE_SIZE * 0.25, TILE_SIZE * 0.8, TILE_SIZE * 0.35);
    
    // Screen border - bright accent
    graphics.lineStyle(2, TERMINAL_COLORS.ACCENT, 1);
    graphics.strokeRect(-TILE_SIZE * 0.4, -TILE_SIZE * 0.25, TILE_SIZE * 0.8, TILE_SIZE * 0.35);
    
    // Control buttons - left side
    graphics.fillStyle(TERMINAL_COLORS.BORDER, 0.8);
    graphics.fillRect(-TILE_SIZE * 0.42, TILE_SIZE * 0.05, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
    graphics.fillRect(-TILE_SIZE * 0.42, TILE_SIZE * 0.15, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
    
    // Control buttons - right side
    graphics.fillRect(TILE_SIZE * 0.34, TILE_SIZE * 0.05, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
    graphics.fillRect(TILE_SIZE * 0.34, TILE_SIZE * 0.15, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
    
    // Center indicator light - pulsing
    graphics.fillStyle(TERMINAL_COLORS.ACCENT_LIGHT, 1);
    graphics.fillCircle(0, TILE_SIZE * 0.35, 2);
    
    // Add graphics to container
    container.add(graphics);
    
    // Create interactive hitbox
    const hitBox = this.scene.add.rectangle(0, 0, TILE_SIZE * 1.1, TILE_SIZE * 1.1, 0x000000, 0);
    hitBox.setInteractive({ useHandCursor: true });
    container.add(hitBox);
    
    // Interaction handlers
    hitBox.on('pointerdown', (pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
      if (pointer.leftButtonDown() && this.callbacks.onTerminalClick) {
        console.log('🖥️ Memory Lattice clicked - opening Architect Conduit UI');
        this.callbacks.onTerminalClick(roomId);
      }
    });
    
    // Hover effect - brighten screen
    hitBox.on('pointerover', () => {
      graphics.clear();
      
      // Redraw with brighter colors
      graphics.lineStyle(3, TERMINAL_COLORS.ACCENT, 1.2);
      graphics.strokeRect(-TILE_SIZE * 0.5, -TILE_SIZE * 0.5, TILE_SIZE, TILE_SIZE);
      
      graphics.lineStyle(1, TERMINAL_COLORS.ACCENT, 1);
      graphics.strokeRect(-TILE_SIZE * 0.45, -TILE_SIZE * 0.45, TILE_SIZE * 0.9, TILE_SIZE * 0.9);
      
      graphics.fillStyle(TERMINAL_COLORS.ACCENT, 0.7);
      graphics.fillRect(-TILE_SIZE * 0.45, -TILE_SIZE * 0.45, TILE_SIZE * 0.9, TILE_SIZE * 0.15);
      
      graphics.fillStyle(TERMINAL_COLORS.ACCENT, 0.5);
      graphics.fillRect(-TILE_SIZE * 0.4, -TILE_SIZE * 0.25, TILE_SIZE * 0.8, TILE_SIZE * 0.35);
      
      graphics.lineStyle(2, TERMINAL_COLORS.ACCENT, 1.5);
      graphics.strokeRect(-TILE_SIZE * 0.4, -TILE_SIZE * 0.25, TILE_SIZE * 0.8, TILE_SIZE * 0.35);
      
      graphics.fillStyle(TERMINAL_COLORS.ACCENT, 1);
      graphics.fillRect(-TILE_SIZE * 0.42, TILE_SIZE * 0.05, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
      graphics.fillRect(-TILE_SIZE * 0.42, TILE_SIZE * 0.15, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
      graphics.fillRect(TILE_SIZE * 0.34, TILE_SIZE * 0.05, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
      graphics.fillRect(TILE_SIZE * 0.34, TILE_SIZE * 0.15, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
      
      graphics.fillStyle(TERMINAL_COLORS.ACCENT_LIGHT, 1);
      graphics.fillCircle(0, TILE_SIZE * 0.35, 3);
    });
    
    hitBox.on('pointerout', () => {
      graphics.clear();
      
      // Redraw with normal colors
      graphics.lineStyle(3, TERMINAL_COLORS.BORDER, 1);
      graphics.strokeRect(-TILE_SIZE * 0.5, -TILE_SIZE * 0.5, TILE_SIZE, TILE_SIZE);
      
      graphics.lineStyle(1, TERMINAL_COLORS.ACCENT, 0.6);
      graphics.strokeRect(-TILE_SIZE * 0.45, -TILE_SIZE * 0.45, TILE_SIZE * 0.9, TILE_SIZE * 0.9);
      
      graphics.fillStyle(TERMINAL_COLORS.BORDER, 0.5);
      graphics.fillRect(-TILE_SIZE * 0.45, -TILE_SIZE * 0.45, TILE_SIZE * 0.9, TILE_SIZE * 0.15);
      
      graphics.fillStyle(TERMINAL_COLORS.ACCENT, 0.2);
      graphics.fillRect(-TILE_SIZE * 0.4, -TILE_SIZE * 0.25, TILE_SIZE * 0.8, TILE_SIZE * 0.35);
      
      graphics.lineStyle(2, TERMINAL_COLORS.ACCENT, 1);
      graphics.strokeRect(-TILE_SIZE * 0.4, -TILE_SIZE * 0.25, TILE_SIZE * 0.8, TILE_SIZE * 0.35);
      
      graphics.fillStyle(TERMINAL_COLORS.BORDER, 0.8);
      graphics.fillRect(-TILE_SIZE * 0.42, TILE_SIZE * 0.05, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
      graphics.fillRect(-TILE_SIZE * 0.42, TILE_SIZE * 0.15, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
      graphics.fillRect(TILE_SIZE * 0.34, TILE_SIZE * 0.05, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
      graphics.fillRect(TILE_SIZE * 0.34, TILE_SIZE * 0.15, TILE_SIZE * 0.08, TILE_SIZE * 0.08);
      
      graphics.fillStyle(SANCTUM_COLORS.LATTICE_PURPLE, 1);
      graphics.fillCircle(0, TILE_SIZE * 0.35, 2);
    });
    
    this.containerLayer!.add(container);
    this.terminalSprite = container;
  }

  /**
   * Clear current room rendering
   */
  clearRoom(): void {
    // Clear enemies
    this.enemies.forEach((enemy: EnemySprite) => enemy.destroy());
    this.enemies.clear();
    
    // Clear containers
    this.containers.forEach((container: ContainerSprite) => container.destroy());
    this.containers.clear();
    
    // Clear terminal
    if (this.terminalSprite) {
      this.terminalSprite.destroy();
      this.terminalSprite = null;
    }
    
    // Clear graphics
    if (this.tileGraphics) {
      this.tileGraphics.clear();
    }
    
    this.currentRoom = null;
  }

  /**
   * Destroy all sanctum rendering resources
   */
  destroy(): void {
    this.clearRoom();
    
    if (this.tileLayer) {
      this.tileLayer.destroy();
      this.tileLayer = null;
    }
    
    if (this.enemyLayer) {
      this.enemyLayer.destroy();
      this.enemyLayer = null;
    }
    
    if (this.containerLayer) {
      this.containerLayer.destroy();
      this.containerLayer = null;
    }
    
    if (this.overheadLayer) {
      this.overheadLayer.destroy();
      this.overheadLayer = null;
    }
    
    // Clear overhead elements
    this.overheadElements.forEach(element => element.destroy());
    this.overheadElements = [];
    
    this.tileGraphics = null;
    
    console.log('🏛️ SanctumRenderer destroyed');
  }

  /**
   * Get current active state
   */
  isActiveRenderer(): boolean {
    return this.isActive;
  }

  /**
   * Get current room
   */
  getCurrentRoom(): SanctumRoom | null {
    return this.currentRoom;
  }

  /**
   * Get collision map for pathfinding validation
   */
  getCollisionMap(): Map<string, boolean> {
    return this.collisionMap;
  }

  /**
   * Build collision map for fog of war calculations
   */
  private buildCollisionMap(layout: DungeonLayout): void {
    this.collisionMap.clear();

    // Mark walkable tiles from corridors
    layout.corridors.forEach((corridor: CorridorData) => {
      corridor.tiles.forEach(tile => {
        this.collisionMap.set(`${tile.tileX},${tile.tileY}`, false);
      });
    });

    // Mark tiles from rooms based on collision map
    layout.rooms.forEach((room: SanctumRoom) => {
      for (let y = 0; y < room.height; y++) {
        for (let x = 0; x < room.width; x++) {
          const worldX = room.tileX + x;
          const worldY = room.tileY + y;
          const isWall = room.collisionMap[y]?.[x] ?? true;
          this.collisionMap.set(`${worldX},${worldY}`, isWall);
        }
      }
    });
  }

  /**
   * Render steam vents with occasional steam leaks
   */
  private renderSteamVents(layout: DungeonLayout): void {
    if (!this.overheadLayer) return;
    
    // Clear existing overhead elements
    this.overheadElements.forEach(element => element.destroy());
    this.overheadElements = [];
    
    // Generate steam vents across rooms
    const seed = Date.now();
    const rng = {
      value: seed,
      next: function() {
        this.value = (this.value * 9301 + 49297) % 233280;
        return this.value / 233280;
      },
      nextInt: function(min: number, max: number) {
        return Math.floor(this.next() * (max - min)) + min;
      }
    };
    
    // Add steam vents to each room
    layout.rooms.forEach((room: SanctumRoom) => {
      const ventCount = rng.next() < 0.5 ? 1 : 2; // 1-2 vents per room
      
      for (let v = 0; v < ventCount; v++) {
        const ventX = room.tileX + rng.nextInt(2, room.width - 2);
        const ventY = room.tileY + rng.nextInt(2, room.height - 2);
        
        // Draw vent grate (small grid pattern)
        const graphics = this.scene.add.graphics();
        const worldX = ventX * TILE_SIZE + TILE_SIZE / 2;
        const worldY = ventY * TILE_SIZE + TILE_SIZE / 2;
        
        graphics.fillStyle(0x4a4a4a, 0.6);
        graphics.fillRect(worldX - 4, worldY - 4, 8, 8);
        graphics.lineStyle(1, 0x2a2a2a, 1);
        graphics.strokeRect(worldX - 4, worldY - 4, 8, 8);
        
        // Draw vent grid
        graphics.lineBetween(worldX - 2, worldY - 4, worldX - 2, worldY + 4);
        graphics.lineBetween(worldX + 2, worldY - 4, worldX + 2, worldY + 4);
        graphics.lineBetween(worldX - 4, worldY - 2, worldX + 4, worldY - 2);
        graphics.lineBetween(worldX - 4, worldY + 2, worldX + 4, worldY + 2);
        
        if (this.overheadLayer) {
          this.overheadLayer.add(graphics);
          graphics.setDepth(25);
          this.overheadElements.push(graphics);
        }
        
        // Occasionally emit steam particles from vent
        this.animateSteamVentLeak(worldX, worldY);
      }
    });
    
    console.log(`🏛️ Rendered steam vents across sanctum rooms`);
  }
  
  /**
   * Animate continuous steam spray from vent
   */
  private animateSteamVentLeak(ventX: number, ventY: number): void {
    const emitSteam = () => {
      // Continuous spray: emit 8-12 particles per cycle
      const particleCount = 8 + Math.floor(Math.random() * 5);
      
      for (let i = 0; i < particleCount; i++) {
        const particle = this.scene.add.graphics();
        this.overheadLayer?.add(particle);
        particle.setDepth(26); // Above most elements
        
        // Larger pixelated steam blocks (8x8 pixels)
        const particleSize = 8;
        const duration = 1200 + Math.random() * 600; // 1200-1800ms (slower rise)
        const delay = i * 15; // Faster emission rate
        
        // Wider spread pattern for continuous spray effect
        const spreadX = (Math.random() - 0.5) * 60;
        const spreadY = -80 - Math.random() * 40;
        
        // Draw initial pixelated block
        particle.fillStyle(0xcccccc, 0.9);
        particle.fillRect(-particleSize / 2, -particleSize / 2, particleSize, particleSize);
        
        this.scene.tweens.add({
          targets: particle,
          x: { from: ventX, to: ventX + spreadX },
          y: { from: ventY, to: ventY + spreadY },
          alpha: { from: 0.9, to: 0 },
          duration,
          delay,
          onUpdate: (tween) => {
            const alpha = (tween.targets[0] as any).alpha;
            particle.clear();
            
            // Draw larger pixelated steam blocks with fading
            particle.fillStyle(0xdddddd, alpha * 0.8);
            particle.fillRect(-particleSize / 2, -particleSize / 2, particleSize, particleSize);
            
            // Add secondary blocks for blocky effect
            if (Math.random() > 0.4) {
              particle.fillStyle(0xcccccc, alpha * 0.6);
              particle.fillRect(-particleSize / 2 - 4, -particleSize / 2, particleSize, particleSize);
            }
            if (Math.random() > 0.5) {
              particle.fillStyle(0xcccccc, alpha * 0.5);
              particle.fillRect(-particleSize / 2, -particleSize / 2 - 4, particleSize, particleSize);
            }
          },
          onComplete: () => {
            particle.destroy();
          },
        });
      }
      
      // Schedule next continuous spray (fast, continuous emission)
      this.scene.time.delayedCall(300, emitSteam); // 300ms between spray cycles
    };
    
    // Start first spray immediately
    emitSteam();
  }
  
  /**
   * Update parallax effect for overhead elements based on camera movement
   */
  public updateOverheadParallax(cameraX: number, cameraY: number): void {
    if (!this.overheadLayer || this.overheadElements.length === 0) return;
    
    // Calculate camera delta
    const deltaX = cameraX - this.lastCameraX;
    const deltaY = cameraY - this.lastCameraY;
    
    // Apply subtle parallax offset (elements move slightly slower than camera)
    if (Math.abs(deltaX) > 0.1 || Math.abs(deltaY) > 0.1) {
      this.overheadElements.forEach((element, index) => {
        // Different parallax factors for variety
        const parallaxFactor = 0.05 + (index % 3) * 0.01;
        element.x -= deltaX * parallaxFactor;
        element.y -= deltaY * parallaxFactor;
      });
    }
    
    this.lastCameraX = cameraX;
    this.lastCameraY = cameraY;
  }

  /**
   * Render steam pipe with blocky particle spray effect
   */
  private renderSteamPipe(pipe: any, room: SanctumRoom): void {
    if (!this.containerLayer) return;

    const worldX = (room.tileX + pipe.tileX) * TILE_SIZE + TILE_SIZE / 2;
    const worldY = (room.tileY + pipe.tileY) * TILE_SIZE + TILE_SIZE / 2;

    // Create pipe body (small grey rectangle)
    const pipeGraphics = this.scene.add.graphics();
    pipeGraphics.fillStyle(0x5a5a5a, 0.8);
    pipeGraphics.fillRect(-3, -3, 6, 6);
    pipeGraphics.lineStyle(1, 0x3a3a3a, 1);
    pipeGraphics.strokeRect(-3, -3, 6, 6);

    const container = this.scene.add.container(worldX, worldY);
    container.add(pipeGraphics);
    container.setDepth(25);
    this.containerLayer.add(container);

    // Create animated blocky steam particles
    const particleCount = Math.floor(3 + pipe.intensity * 2); // 3-5 particles
    const particles: Phaser.GameObjects.Graphics[] = [];

    for (let i = 0; i < particleCount; i++) {
      const particle = this.scene.add.graphics();
      particles.push(particle);
      this.containerLayer.add(particle);

      // Animate particle spray
      this.animateSteamParticle(particle, worldX, worldY, pipe.direction, pipe.intensity, i);
    }

    this.steamPipeEffects.set(pipe.id, { graphics: pipeGraphics, particles });
  }

  /**
   * Animate blocky steam particle with spray effect
   */
  private animateSteamParticle(
    particle: Phaser.GameObjects.Graphics,
    originX: number,
    originY: number,
    direction: 'up' | 'down' | 'left' | 'right',
    intensity: number,
    index: number
  ): void {
    const particleSize = 3 + Math.random() * 2; // 3-5px blocky squares
    const duration = 800 + Math.random() * 400; // 800-1200ms
    const delay = index * 100; // Stagger particle spawning

    // Calculate spray direction
    let velocityX = 0;
    let velocityY = 0;
    const spreadAngle = (Math.random() - 0.5) * 0.6; // ±30 degrees spread

    switch (direction) {
      case 'up':
        velocityY = -80 * intensity;
        velocityX = Math.sin(spreadAngle) * 40 * intensity;
        break;
      case 'down':
        velocityY = 80 * intensity;
        velocityX = Math.sin(spreadAngle) * 40 * intensity;
        break;
      case 'left':
        velocityX = -80 * intensity;
        velocityY = Math.sin(spreadAngle) * 40 * intensity;
        break;
      case 'right':
        velocityX = 80 * intensity;
        velocityY = Math.sin(spreadAngle) * 40 * intensity;
        break;
    }

    // Draw initial blocky particle
    const drawParticle = (x: number, y: number, alpha: number) => {
      particle.clear();
      particle.fillStyle(0xcccccc, alpha * 0.7); // Light grey steam
      particle.fillRect(x - particleSize / 2, y - particleSize / 2, particleSize, particleSize);
    };

    // Animate particle spray
    this.scene.tweens.add({
      targets: particle,
      x: { from: originX, to: originX + velocityX * (duration / 1000) },
      y: { from: originY, to: originY + velocityY * (duration / 1000) },
      alpha: { from: 0.8, to: 0 },
      duration,
      delay,
      onUpdate: (tween) => {
        const progress = tween.progress;
        const currentAlpha = 0.8 * (1 - progress);
        drawParticle(particle.x, particle.y, currentAlpha);
      },
      onComplete: () => {
        particle.destroy();
      },
    });

    // Loop animation
    this.scene.time.delayedCall(delay + duration + 200, () => {
      if (particle.active) {
        this.animateSteamParticle(particle, originX, originY, direction, intensity, index);
      }
    });
  }

  /**
   * Render wall lights with simulated light effects
   */
  private renderWallLights(layout: DungeonLayout): void {
    if (!this.containerLayer) return;

    // Create light overlay for simulated lighting
    this.lightOverlay = this.scene.add.graphics();
    this.lightOverlay.setDepth(1050); // Above player but below telegraphs
    this.containerLayer.add(this.lightOverlay);

    // Scan all rooms for wall lights
    layout.rooms.forEach((room: SanctumRoom) => {
      for (let y = 0; y < room.height; y++) {
        for (let x = 0; x < room.width; x++) {
          const tileType = room.tiles[y]?.[x];
          
          // Check if this is a wall light tile (type 33)
          if (tileType === 33) {
            const worldX = (room.tileX + x) * TILE_SIZE + TILE_SIZE / 2;
            const worldY = (room.tileY + y) * TILE_SIZE + TILE_SIZE / 2;
            
            this.renderWallLight(worldX, worldY);
          }
        }
      }
    });
  }

  /**
   * Render individual wall light with glow effect and light rays
   */
  private renderWallLight(worldX: number, worldY: number): void {
    if (!this.containerLayer) return;

    // Create light fixture (small yellow square on wall)
    const lightGraphics = this.scene.add.graphics();
    lightGraphics.fillStyle(0xffff99, 0.9); // Bright yellow
    lightGraphics.fillRect(worldX - 3, worldY - 3, 6, 6);
    lightGraphics.lineStyle(1, 0xffcc00, 1);
    lightGraphics.strokeRect(worldX - 3, worldY - 3, 6, 6);

    this.containerLayer.add(lightGraphics);
    lightGraphics.setDepth(25);

    // Create light rays (4 directional rays)
    const rayGraphics = this.scene.add.graphics();
    rayGraphics.lineStyle(1, 0xffff99, 0.4);
    const rayLength = TILE_SIZE * 3;
    
    // Rays in 4 directions (up, down, left, right)
    rayGraphics.lineBetween(worldX, worldY - 3, worldX, worldY - rayLength);
    rayGraphics.lineBetween(worldX, worldY + 3, worldX, worldY + rayLength);
    rayGraphics.lineBetween(worldX - 3, worldY, worldX - rayLength, worldY);
    rayGraphics.lineBetween(worldX + 3, worldY, worldX + rayLength, worldY);
    
    this.containerLayer.add(rayGraphics);
    rayGraphics.setDepth(23); // Below light fixture

    // Create simulated light glow (circular gradient effect)
    const glowGraphics = this.scene.add.graphics();
    const glowRadius = TILE_SIZE * 4; // 4-tile light radius

    // Draw concentric circles for glow effect
    for (let i = 0; i < 5; i++) {
      const radius = (glowRadius * (i + 1)) / 5;
      const alpha = 0.15 * (1 - i / 5); // Fade out
      glowGraphics.fillStyle(0xffff99, alpha);
      glowGraphics.fillCircle(worldX, worldY, radius);
    }

    this.containerLayer.add(glowGraphics);
    glowGraphics.setDepth(24); // Below light fixture

    // Animate subtle pulsing effect
    this.scene.tweens.add({
      targets: lightGraphics,
      alpha: { from: 0.9, to: 0.7 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    });

    this.wallLights.set(`light_${worldX}_${worldY}`, lightGraphics);
  }

  /**
   * Render environmental effects (sparks, drips, fire, gas, corrosion)
   */
  private renderEnvironmentalEffects(layout: DungeonLayout): void {
    if (!this.containerLayer) return;

    // Scan all rooms for environmental effects
    layout.rooms.forEach((room: SanctumRoom) => {
      if (!room.environmentalEffects) return;
      
      room.environmentalEffects.forEach((effect: any) => {
        const worldX = (room.tileX + effect.tileX) * TILE_SIZE + TILE_SIZE / 2;
        const worldY = (room.tileY + effect.tileY) * TILE_SIZE + TILE_SIZE / 2;
        
        switch (effect.type) {
          case 'spark':
            this.renderSparkingElectricity(worldX, worldY, effect.intensity);
            break;
          case 'drip':
            this.renderWaterDrip(worldX, worldY, effect.intensity);
            break;
          case 'fire':
            this.renderFire(worldX, worldY, effect.intensity);
            break;
          case 'gas':
            this.renderGasLeak(worldX, worldY, effect.intensity);
            break;
          case 'corrosion':
            this.renderCorrosion(worldX, worldY, effect.intensity);
            break;
        }
      });
    });
  }

  /**
   * Render sparking electricity effect
   */
  private renderSparkingElectricity(x: number, y: number, intensity: number): void {
    const emitSparks = () => {
      const sparkCount = 3 + Math.floor(Math.random() * 3); // 3-5 sparks per burst
      
      for (let i = 0; i < sparkCount; i++) {
        const spark = this.scene.add.graphics();
        this.overheadLayer?.add(spark);
        spark.setDepth(26);
        
        const sparkSize = 3;
        const duration = 400 + Math.random() * 200; // 400-600ms
        const delay = i * 20;
        
        // Random spread pattern
        const spreadX = (Math.random() - 0.5) * 30;
        const spreadY = (Math.random() - 0.5) * 30;
        
        // Draw initial spark (bright yellow)
        spark.fillStyle(0xffff00, 1);
        spark.fillRect(-sparkSize / 2, -sparkSize / 2, sparkSize, sparkSize);
        
        this.scene.tweens.add({
          targets: spark,
          x: { from: x, to: x + spreadX },
          y: { from: y, to: y + spreadY },
          alpha: { from: 1, to: 0 },
          duration,
          delay,
          onUpdate: (tween) => {
            const alpha = (tween.targets[0] as any).alpha;
            spark.clear();
            
            // Draw spark with fading
            spark.fillStyle(0xffff00, alpha * 0.9);
            spark.fillRect(-sparkSize / 2, -sparkSize / 2, sparkSize, sparkSize);
            
            // Add secondary spark for flicker
            if (Math.random() > 0.6) {
              spark.fillStyle(0xffaa00, alpha * 0.7);
              spark.fillRect(-sparkSize / 2 - 1, -sparkSize / 2, sparkSize, sparkSize);
            }
          },
          onComplete: () => {
            spark.destroy();
          },
        });
      }
      
      // Schedule next spark burst (faster than steam)
      this.scene.time.delayedCall(200 + Math.random() * 300, emitSparks);
    };
    
    emitSparks();
  }

  /**
   * Render water drip effect
   */
  private renderWaterDrip(x: number, y: number, intensity: number): void {
    const emitDrips = () => {
      const dripCount = 2 + Math.floor(Math.random() * 2); // 2-3 drips per cycle
      
      for (let i = 0; i < dripCount; i++) {
        const drip = this.scene.add.graphics();
        this.overheadLayer?.add(drip);
        drip.setDepth(26);
        
        const dripSize = 3;
        const duration = 600 + Math.random() * 300; // 600-900ms
        const delay = i * 100;
        
        // Drips fall straight down with slight spread
        const spreadX = (Math.random() - 0.5) * 10;
        const spreadY = 40 + Math.random() * 20;
        
        // Draw initial drip (light blue)
        drip.fillStyle(0x87ceeb, 0.8);
        drip.fillRect(-dripSize / 2, -dripSize / 2, dripSize, dripSize);
        
        this.scene.tweens.add({
          targets: drip,
          x: { from: x, to: x + spreadX },
          y: { from: y, to: y + spreadY },
          alpha: { from: 0.8, to: 0 },
          duration,
          delay,
          onUpdate: (tween) => {
            const alpha = (tween.targets[0] as any).alpha;
            drip.clear();
            
            // Draw drip with fading
            drip.fillStyle(0x87ceeb, alpha * 0.8);
            drip.fillRect(-dripSize / 2, -dripSize / 2, dripSize, dripSize);
          },
          onComplete: () => {
            drip.destroy();
          },
        });
      }
      
      // Schedule next drip cycle
      this.scene.time.delayedCall(500 + Math.random() * 1000, emitDrips);
    };
    
    emitDrips();
  }

  /**
   * Render fire effect
   */
  private renderFire(x: number, y: number, intensity: number): void {
    const emitFlames = () => {
      const flameCount = 4 + Math.floor(Math.random() * 3); // 4-6 flames per burst
      
      for (let i = 0; i < flameCount; i++) {
        const flame = this.scene.add.graphics();
        this.overheadLayer?.add(flame);
        flame.setDepth(26);
        
        const flameSize = 6;
        const duration = 800 + Math.random() * 400; // 800-1200ms
        const delay = i * 25;
        
        // Flames rise upward with spread
        const spreadX = (Math.random() - 0.5) * 40;
        const spreadY = -60 - Math.random() * 30;
        
        // Draw initial flame (orange-red)
        flame.fillStyle(0xff6600, 0.9);
        flame.fillRect(-flameSize / 2, -flameSize / 2, flameSize, flameSize);
        
        this.scene.tweens.add({
          targets: flame,
          x: { from: x, to: x + spreadX },
          y: { from: y, to: y + spreadY },
          alpha: { from: 0.9, to: 0 },
          duration,
          delay,
          onUpdate: (tween) => {
            const alpha = (tween.targets[0] as any).alpha;
            flame.clear();
            
            // Draw flame with color transition
            const color = alpha > 0.5 ? 0xff6600 : 0xffaa00; // Orange to yellow
            flame.fillStyle(color, alpha * 0.8);
            flame.fillRect(-flameSize / 2, -flameSize / 2, flameSize, flameSize);
            
            // Add secondary flame for flicker
            if (Math.random() > 0.5) {
              flame.fillStyle(0xffaa00, alpha * 0.6);
              flame.fillRect(-flameSize / 2 - 2, -flameSize / 2, flameSize, flameSize);
            }
          },
          onComplete: () => {
            flame.destroy();
          },
        });
      }
      
      // Schedule next flame burst
      this.scene.time.delayedCall(300 + Math.random() * 400, emitFlames);
    };
    
    emitFlames();
  }

  /**
   * Render gas leak effect
   */
  private renderGasLeak(x: number, y: number, intensity: number): void {
    const emitGas = () => {
      const gasCount = 6 + Math.floor(Math.random() * 4); // 6-9 gas particles per cycle
      
      for (let i = 0; i < gasCount; i++) {
        const gas = this.scene.add.graphics();
        this.overheadLayer?.add(gas);
        gas.setDepth(26);
        
        const gasSize = 5;
        const duration = 1000 + Math.random() * 500; // 1000-1500ms
        const delay = i * 12;
        
        // Gas spreads in all directions
        const angle = (Math.random() * Math.PI * 2);
        const distance = 30 + Math.random() * 40;
        const spreadX = Math.cos(angle) * distance;
        const spreadY = Math.sin(angle) * distance;
        
        // Draw initial gas cloud (greenish)
        gas.fillStyle(0x99dd00, 0.7);
        gas.fillRect(-gasSize / 2, -gasSize / 2, gasSize, gasSize);
        
        this.scene.tweens.add({
          targets: gas,
          x: { from: x, to: x + spreadX },
          y: { from: y, to: y + spreadY },
          alpha: { from: 0.7, to: 0 },
          duration,
          delay,
          onUpdate: (tween) => {
            const alpha = (tween.targets[0] as any).alpha;
            gas.clear();
            
            // Draw gas with fading
            gas.fillStyle(0x99dd00, alpha * 0.7);
            gas.fillRect(-gasSize / 2, -gasSize / 2, gasSize, gasSize);
          },
          onComplete: () => {
            gas.destroy();
          },
        });
      }
      
      // Schedule next gas burst
      this.scene.time.delayedCall(400, emitGas);
    };
    
    emitGas();
  }

  /**
   * Render corrosion effect (acid/rust)
   */
  private renderCorrosion(x: number, y: number, intensity: number): void {
    const emitCorrosion = () => {
      const particleCount = 3 + Math.floor(Math.random() * 2); // 3-4 particles per burst
      
      for (let i = 0; i < particleCount; i++) {
        const particle = this.scene.add.graphics();
        this.overheadLayer?.add(particle);
        particle.setDepth(26);
        
        const particleSize = 4;
        const duration = 700 + Math.random() * 300; // 700-1000ms
        const delay = i * 50;
        
        // Corrosion drips downward
        const spreadX = (Math.random() - 0.5) * 15;
        const spreadY = 30 + Math.random() * 20;
        
        // Draw initial corrosion (purple-ish)
        particle.fillStyle(0x9966cc, 0.8);
        particle.fillRect(-particleSize / 2, -particleSize / 2, particleSize, particleSize);
        
        this.scene.tweens.add({
          targets: particle,
          x: { from: x, to: x + spreadX },
          y: { from: y, to: y + spreadY },
          alpha: { from: 0.8, to: 0 },
          duration,
          delay,
          onUpdate: (tween) => {
            const alpha = (tween.targets[0] as any).alpha;
            particle.clear();
            
            // Draw corrosion with fading
            particle.fillStyle(0x9966cc, alpha * 0.8);
            particle.fillRect(-particleSize / 2, -particleSize / 2, particleSize, particleSize);
          },
          onComplete: () => {
            particle.destroy();
          },
        });
      }
      
      // Schedule next corrosion burst
      this.scene.time.delayedCall(800 + Math.random() * 400, emitCorrosion);
    };
    
    emitCorrosion();
  }

  /**
   * Update fog of war based on player position
   */
  public updateFogOfWar(playerX: number, playerY: number): void {
    const playerTileX = Math.floor(playerX / TILE_SIZE);
    const playerTileY = Math.floor(playerY / TILE_SIZE);

    // Calculate visibility using shadowcasting
    this.fogOfWar.calculateVisibility(
      playerTileX,
      playerTileY,
      (x, y) => {
        // Return true if blocked (wall)
        return this.collisionMap.get(`${x},${y}`) ?? true;
      }
    );

    // Render fog of war overlay
    this.renderFogOfWar();
  }

  /**
   * Render fog of war overlay
   */
  private renderFogOfWar(): void {
    if (!this.fogGraphics || !this.currentDungeonLayout) return;

    this.fogGraphics.clear();

    // Find dungeon bounds
    const layout = this.currentDungeonLayout;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    layout.rooms.forEach((room: SanctumRoom) => {
      minX = Math.min(minX, room.tileX);
      minY = Math.min(minY, room.tileY);
      maxX = Math.max(maxX, room.tileX + room.width);
      maxY = Math.max(maxY, room.tileY + room.height);
    });

    const padding = 20;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    // Draw fog over non-visible tiles
    for (let tileY = minY; tileY < maxY; tileY++) {
      for (let tileX = minX; tileX < maxX; tileX++) {
        const isVisible = this.fogOfWar.isVisible(tileX, tileY);
        const isExplored = this.fogOfWar.isExplored(tileX, tileY);

        if (!isVisible) {
          const worldX = tileX * TILE_SIZE;
          const worldY = tileY * TILE_SIZE;

          if (isExplored) {
            // Discovered but out of view: semi-transparent fog (0.9 opacity)
            this.fogGraphics.fillStyle(0x000000, 0.6);
          } else {
            // Completely undiscovered: full darkness (1.0 opacity)
            this.fogGraphics.fillStyle(0x000000, 1.0);
          }

          this.fogGraphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
        }
      }
    }
  }

  /**
   * Render boss telegraph (attack warning)
   */
  renderTelegraph(
    telegraphId: string,
    centerTileX: number,
    centerTileY: number,
    radius: number,
    color: number,
    duration: number,
    type: string
  ): void {
    if (!this.telegraphLayer) return;

    // Create telegraph graphics
    const telegraph = this.scene.add.graphics();
    const worldX = centerTileX * TILE_SIZE + TILE_SIZE / 2;
    const worldY = centerTileY * TILE_SIZE + TILE_SIZE / 2;
    const worldRadius = radius * TILE_SIZE;

    // Different visual styles based on attack type
    if (type === 'aoe_blast') {
      // Pulsing circle for AOE attacks
      telegraph.lineStyle(4, color, 0.8);
      telegraph.strokeCircle(0, 0, worldRadius);
      telegraph.fillStyle(color, 0.2);
      telegraph.fillCircle(0, 0, worldRadius);
    } else if (type === 'targeted_strike') {
      // Sharp rectangle for targeted attacks
      telegraph.lineStyle(4, color, 0.8);
      telegraph.strokeRect(-worldRadius / 2, -worldRadius / 2, worldRadius, worldRadius);
      telegraph.fillStyle(color, 0.25);
      telegraph.fillRect(-worldRadius / 2, -worldRadius / 2, worldRadius, worldRadius);
    } else if (type === 'area_denial') {
      // Hazard pattern for area denial
      telegraph.lineStyle(3, color, 0.6);
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const x = Math.cos(angle) * worldRadius;
        const y = Math.sin(angle) * worldRadius;
        telegraph.lineBetween(0, 0, x, y);
      }
      telegraph.fillStyle(color, 0.15);
      telegraph.fillCircle(0, 0, worldRadius);
    } else {
      // Default: simple circle
      telegraph.lineStyle(3, color, 0.7);
      telegraph.strokeCircle(0, 0, worldRadius);
      telegraph.fillStyle(color, 0.2);
      telegraph.fillCircle(0, 0, worldRadius);
    }

    telegraph.setPosition(worldX, worldY);
    this.telegraphLayer.add(telegraph);
    this.activeTelegraphs.set(telegraphId, telegraph);

    // Pulse animation
    this.scene.tweens.add({
      targets: telegraph,
      alpha: { from: 0.3, to: 1.0 },
      duration: 300,
      yoyo: true,
      repeat: Math.floor(duration / 600), // Pulse for duration
    });

    // Remove after duration
    setTimeout(() => {
      this.removeTelegraph(telegraphId);
    }, duration);

    console.log(`🏛️ Rendered telegraph ${telegraphId} at (${centerTileX}, ${centerTileY}) with radius ${radius}`);
  }

  /**
   * Remove telegraph from renderer
   */
  removeTelegraph(telegraphId: string): void {
    const telegraph = this.activeTelegraphs.get(telegraphId);
    if (telegraph) {
      telegraph.destroy();
      this.activeTelegraphs.delete(telegraphId);
    }
  }

  /**
   * Clear all active telegraphs
   */
  clearAllTelegraphs(): void {
    this.activeTelegraphs.forEach((telegraph) => {
      telegraph.destroy();
    });
    this.activeTelegraphs.clear();
  }
}
